import { useCallback } from 'react'
import { useQueryClient } from 'react-query'
import { useRegenerate } from 'hooks/queries/designs/useRegenerate'
import { useVideoRegenerate } from 'hooks/queries/videos/useVideoRegenerate'
import { useCurrentWorkspaceId } from 'hooks/useCurrentWorkspaceId'
import { useRootStore } from 'stores/RootStore'
import { filesKeys } from 'hooks/queries/files/filesKeys'
import { optimisticUpdateFileInInfiniteLists, updateFileDetailCache } from 'hooks/queries/files/cacheUtils'
import { AlmanacFileShowItem } from 'hooks/queries/files/types'
import { showToastMessage } from 'common/ToastMessage/ShowToastMessage'
import type { GenerationType } from 'stores/GenerationTimestampStore'
import { AIInsufficientCreditsError } from 'api/clients/AiClient'
import { AvatarRegenerationParams } from 'utils/avatarScriptSettings'
import { VideoRegenerationParams } from 'utils/videoScriptSettings'
import { AssetType } from 'hooks/useAlmanacFileSubscription'

interface UsePerformRegenerationOptions {
  fileId: string
  assetType?: string | null
  isAiGenerated: boolean
  onSuccess?: () => void
  onError?: (error: string) => void
}

export type { AvatarRegenerationParams, VideoRegenerationParams }

interface PerformRegenerationParams {
  mediaFileKey: string | null
  topic: string
  mediaSourceOverride?: 'ai_video' | 'ai_image' | 'default'
  avatarParams?: AvatarRegenerationParams
  videoParams?: VideoRegenerationParams
}

export const usePerformRegeneration = ({
  fileId,
  assetType,
  isAiGenerated,
  onSuccess,
  onError,
}: UsePerformRegenerationOptions) => {
  const queryClient = useQueryClient()
  const workspaceId = useCurrentWorkspaceId()
  const { generationTimestampStore } = useRootStore()
  const isVideoAsset = assetType === AssetType.Video
  const { mutateAsync: regenerateDesign, isLoading: isDesignLoading } = useRegenerate({ designId: fileId })
  const { mutateAsync: regenerateVideo, isLoading: isVideoLoading } = useVideoRegenerate({ videoId: fileId })

  const performRegeneration = useCallback(
    async (params: PerformRegenerationParams) => {
      const regenerate = isVideoAsset ? regenerateVideo : regenerateDesign

      try {
        await regenerate({
          media_file_key: params.mediaFileKey as string,
          topic: params.topic,
          media_source_override: params.mediaSourceOverride,
          ...params.avatarParams,
          ...params.videoParams,
        })

        optimisticUpdateFileInInfiniteLists(queryClient, workspaceId, fileId, (f) => ({
          ...f,
          is_generating: true,
          image_url_generating: true,
        }))

        updateFileDetailCache<AlmanacFileShowItem>(queryClient, workspaceId, fileId, (file) => ({
          ...file,
          is_generating: true,
          image_url_generating: true,
        }))

        queryClient.invalidateQueries({ queryKey: filesKeys.detail(workspaceId, fileId) })

        const generationType: GenerationType = isAiGenerated ? 'ai' : 'default'
        generationTimestampStore.startGeneration(fileId, generationType)

        onSuccess?.()
      } catch (error) {
        if (error instanceof AIInsufficientCreditsError) {
          const errorMessage = 'Insufficient credits. Please purchase more credits to continue.'
          onError?.(errorMessage)
          showToastMessage('Insufficient credits', 'error')
          return
        }
        const errorMessage = 'Failed to start regeneration. Please try again.'
        onError?.(errorMessage)
        showToastMessage('Failed to start regeneration', 'error')
      }
    },
    [
      regenerateDesign,
      regenerateVideo,
      isVideoAsset,
      queryClient,
      workspaceId,
      fileId,
      isAiGenerated,
      generationTimestampStore,
      onSuccess,
      onError,
    ],
  )

  return {
    performRegeneration,
    isLoading: isVideoAsset ? isVideoLoading : isDesignLoading,
  }
}
